/**
 * Developer Tools — the API as this build actually routes it, plus the keys
 * for calling it.
 *
 * Staff only, enforced server-side: a non-staff caller gets 404 from the JSON
 * endpoint whether or not the rail drew the link.
 *
 * The reference implementation of this page is a keys-only screen; this one
 * keeps the route inventory too, because the inventory is the part that
 * cannot be reconstructed from memory and the part people actually come here
 * for. Styling follows the same Signal tokens.
 */
import { Fragment, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import './api-routes.css'

type Param = {
  name: string
  type: string | null
  required: boolean | null
  description: string
}
type Route = {
  rule: string
  methods: string[]
  guard: string
  guard_label: string
  org_scoped: boolean
  summary: string | null
  description: string | null
  params: { path: Param[]; query: Param[]; body: Param[]; form: Param[] }
  returns: string | null
  responses: Record<string, string>
  documented: boolean
}
type Group = { blueprint: string; routes: Route[] }
/* Migration 038 renamed api_keys to api_tokens and this type did not follow.
   The page destructured `api_keys` off a payload that carries `api_tokens`,
   got undefined, and threw on `.length` — a blank screen from one renamed
   field. `total_requests` went with the table: it was a write on every
   authenticated request, replaced by a throttled last_used_at. */
type ApiTokenRow = {
  id: number
  name: string | null
  kind: string
  token_prefix: string
  scopes: string[] | null
  created_at: string | null
  expires_at: string | null
  last_used_at: string | null
  status: string
}
type Payload = {
  census: { total: number; public: number; org_scoped: number; documented: number; by_guard: Record<string, number> }
  groups: Group[]
  recorded_revision: string | null
  recorded_at: string | null
  api_tokens: ApiTokenRow[]
  user: { email: string } | null
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; data: Payload }
  | { status: 'error'; message: string }

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—')

/** What the route list is narrowed to. `null` is everything. */
type Filter =
  | { kind: 'public' }
  | { kind: 'org_scoped' }
  | { kind: 'guard'; guard: string }

const sameFilter = (a: Filter | null, b: Filter) =>
  a?.kind === b.kind && (a.kind !== 'guard' || a.guard === (b as { guard: string }).guard)

/**
 * A number with a label, and — when it can narrow the list below — a button.
 *
 * `onClick` is what makes it interactive, so the ROUTES box stays inert: it
 * is the total, and "filter to everything" is what clearing does. A control
 * that looks clickable and does nothing is worse than a plain figure.
 */
function Stat({ n, label, alarm, active, onClick }: {
  n: number; label: string; alarm?: boolean
  active?: boolean; onClick?: () => void
}) {
  const className = `dt-stat${alarm ? ' is-alarm' : ''}`
    + `${onClick ? ' is-clickable' : ''}${active ? ' is-active' : ''}`
  const body = (
    <>
      <div className="dt-stat__n t-metric">{n}</div>
      <div className="dt-stat__l t-label">{label}</div>
    </>
  )
  if (!onClick) return <div className={className}>{body}</div>
  return (
    <button type="button" className={className} onClick={onClick}
            aria-pressed={!!active}>
      {body}
    </button>
  )
}

/**
 * Coerce whatever the API sent into the shape this page renders.
 *
 * ONE PLACE, at the boundary, rather than `?? []` at each of the six reads
 * below. The reads are the symptom; the cause is that a payload field can go
 * missing — renamed by a migration, dropped by a guard, absent on an older
 * image — and every unguarded read of it takes the whole page down with a
 * blank screen and a console error.
 *
 * That already happened once: migration 038 renamed api_keys to api_tokens
 * and `api_keys.length` threw. Guarding only that line would have left five
 * more with the same defect — census.by_guard, groups.map, and g.routes on
 * every group.
 *
 * This page's job is to describe the API. Describing it incompletely is
 * useful; disappearing is not.
 */
function normalize(data: Partial<Payload> | null | undefined): Payload {
  const census = data?.census
  return {
    census: {
      total: census?.total ?? 0,
      public: census?.public ?? 0,
      org_scoped: census?.org_scoped ?? 0,
      documented: census?.documented ?? 0,
      by_guard: census?.by_guard ?? {},
    },
    // Groups AND the routes inside them: a group with no route list is the
    // same class of failure one level down — and each route's params, one
    // further. An older image sends routes without them.
    groups: (data?.groups ?? []).map((g) => ({
      blueprint: g?.blueprint ?? 'unknown',
      routes: (g?.routes ?? []).map((r) => ({
        ...r,
        params: {
          path: r?.params?.path ?? [],
          query: r?.params?.query ?? [],
          body: r?.params?.body ?? [],
          form: r?.params?.form ?? [],
        },
        responses: r?.responses ?? {},
        documented: r?.documented ?? false,
      })),
    })),
    recorded_revision: data?.recorded_revision ?? null,
    recorded_at: data?.recorded_at ?? null,
    api_tokens: data?.api_tokens ?? [],
    user: data?.user ?? null,
  }
}

/** One params section (Path/Query/Body/Form) of an opened route row. */
function ParamRows({ label, params }: { label: string; params: Param[] }) {
  if (params.length === 0) return null
  return (
    <div className="dt-detail__block">
      <div className="dt-detail__label t-label">{label}</div>
      <table className="dt-params">
        <tbody>
          {params.map((p) => (
            <tr key={p.name}>
              <td className="dt-params__name">{p.name}</td>
              <td className="dt-params__type t-data">
                {p.type ?? ''}{p.required === true ? ' · required' : ''}
                {p.required === false ? ' · optional' : ''}
              </td>
              <td className="muted">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * What a route takes and what it answers, under its row when opened.
 *
 * Everything here is parsed from the view's own docstring server-side; an
 * undocumented route says so instead of pretending there is nothing to know.
 */
function RouteDetail({ r }: { r: Route }) {
  const statuses = Object.entries(r.responses)
  return (
    <div className="dt-detail">
      {r.description && <p className="dt-detail__desc">{r.description}</p>}
      <ParamRows label="Path" params={r.params.path} />
      <ParamRows label="Query" params={r.params.query} />
      <ParamRows label="Body (JSON)" params={r.params.body} />
      <ParamRows label="Form" params={r.params.form} />
      {statuses.length > 0 ? (
        <div className="dt-detail__block">
          <div className="dt-detail__label t-label">Responses</div>
          <table className="dt-params">
            <tbody>
              {statuses.map(([status, text]) => (
                <tr key={status}>
                  <td className="dt-params__name t-data">{status}</td>
                  <td className="muted" colSpan={2}>{text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : r.returns ? (
        <div className="dt-detail__block">
          <div className="dt-detail__label t-label">Returns</div>
          <pre className="dt-detail__returns">{r.returns}</pre>
        </div>
      ) : null}
      {!r.documented && (
        <p className="muted dt-note">
          This route&rsquo;s docstring does not document its inputs or outputs
          yet — the source is the authority.
        </p>
      )}
    </div>
  )
}

export default function ApiRoutes() {
  const [state, setState] = useState<State>({ status: 'loading' })
  const [open, setOpen] = useState<string | null>(null)
  const [openRoute, setOpenRoute] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter | null>(null)

  useEffect(() => {
    api<Partial<Payload>>('/api/v1/developer-tools')
      .then((data) => setState({ status: 'ready', data: normalize(data) }))
      .catch((e: unknown) => setState({
        status: 'error',
        message: e instanceof ApiError ? e.message : String(e),
      }))
  }, [])

  if (state.status === 'loading') return <p className="muted">Loading…</p>
  if (state.status === 'error') {
    return <div className="panel error">Could not load — {state.message}</div>
  }

  const { census, groups, recorded_revision, recorded_at, api_tokens } = state.data

  const toggle = (next: Filter) =>
    setFilter((current) => (sameFilter(current, next) ? null : next))

  /* Groups with their routes narrowed, and empty groups dropped — a
     blueprint header claiming 0 is noise when you have asked to see only the
     unguarded ones. */
  const matches = (r: Route) =>
    !filter
    || (filter.kind === 'public' && r.guard === 'public')
    || (filter.kind === 'org_scoped' && r.org_scoped)
    || (filter.kind === 'guard' && r.guard === filter.guard)

  const shown = groups
    .map((g) => ({ ...g, routes: g.routes.filter(matches) }))
    .filter((g) => g.routes.length > 0)

  /* Filtering opens everything it kept. Asking for the unguarded routes and
     then being made to click through blueprints to find them would be the
     filter doing half its job. */
  const expanded = (blueprint: string) => (filter ? true : open === blueprint)

  return (
    <>
      <h2 className="st-h2">Routes and tests</h2>
      <p className="page-sub">
        Priori staff only. The reference below is read from the running
        application&rsquo;s routing table, not written by hand — it cannot
        describe a route that no longer exists.
        {recorded_revision
          ? <> The build document recorded the same inventory at{' '}
              <code>{recorded_revision}</code>{recorded_at ? ` (${day(recorded_at)})` : ''}.</>
          : ' This image published no build document, so this is live from this process only.'}
      </p>

      <section className="dt-section">
        <h2 className="dt-h2">API tokens</h2>
        {api_tokens.length === 0 ? (
          <div className="panel muted">No API tokens for this organization yet.</div>
        ) : (
          <div className="dt-keys">
            {api_tokens.map((k) => (
              <div className="dt-key" key={k.id}>
                <div>
                  <div className="dt-key__name">{k.name || 'Unnamed token'}</div>
                  <span className="dt-key__prefix">{k.token_prefix}…</span>
                </div>
                <div className="dt-key__meta t-data">
                  <span>{k.kind}</span>
                  <span>created {day(k.created_at)}</span>
                  <span>used {k.last_used_at ? day(k.last_used_at) : 'never'}</span>
                  <span>expires {day(k.expires_at)}</span>
                  <span className={k.status === 'active' ? 'dt-ok' : 'dt-revoked'}>
                    {k.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="muted dt-note">
          Tokens are created and revoked in <a href="/api-tokens">API tokens</a>.
          The full token is shown once, at creation; only its hash is stored, so a
          lost token is replaced rather than recovered.
        </p>
      </section>

      <section className="dt-section">
        <h2 className="dt-h2">API surface</h2>
        {/* The boxes and chips narrow the list below. This screen exists to
            answer "which routes answer without a guard", and the honest
            answer is the ROUTES, not the count — so the count is the way to
            them. ROUTES stays inert: it is the total, and filtering to
            everything is what clearing does. */}
        <div className="dt-stats">
          <Stat n={census.total} label="routes" />
          <Stat n={census.public} label="public — no guard" alarm
                active={sameFilter(filter, { kind: 'public' })}
                onClick={() => toggle({ kind: 'public' })} />
          <Stat n={census.org_scoped} label="org-scoped"
                active={sameFilter(filter, { kind: 'org_scoped' })}
                onClick={() => toggle({ kind: 'org_scoped' })} />
          {/* api_key became api_token with migration 038; this stat read the
              retired kind and showed 0 forever. */}
          <Stat n={census.by_guard.api_token ?? 0} label="API-token routes"
                active={sameFilter(filter, { kind: 'guard', guard: 'api_token' })}
                onClick={() => toggle({ kind: 'guard', guard: 'api_token' })} />
          <Stat n={census.documented} label="documented" />
        </div>

        <div className="dt-guardbar">
          {Object.entries(census.by_guard).map(([guard, n]) => (
            <button key={guard} type="button"
                    className={`dt-chip is-clickable${guard === 'public' ? ' is-alarm' : ''}`
                      + `${sameFilter(filter, { kind: 'guard', guard }) ? ' is-active' : ''}`}
                    aria-pressed={sameFilter(filter, { kind: 'guard', guard })}
                    onClick={() => toggle({ kind: 'guard', guard })}>
              <span className="t-data">{n}</span> {guard.replace(/_/g, ' ')}
            </button>
          ))}
          {filter && (
            <button type="button" className="dt-chip dt-chip--clear"
                    onClick={() => setFilter(null)}>
              clear filter
            </button>
          )}
        </div>

        {filter && (
          <p className="muted dt-note">
            Showing {shown.reduce((n, g) => n + g.routes.length, 0)} of{' '}
            {census.total} routes.
            {shown.length === 0 && ' Nothing matches — the count above is of a '
              + 'different thing than the route list, which is worth knowing.'}
          </p>
        )}

        {shown.map((g) => (
          <div className="dt-group" key={g.blueprint}>
            <button
              className="dt-group__head"
              aria-expanded={expanded(g.blueprint)}
              onClick={() => setOpen(open === g.blueprint ? null : g.blueprint)}
            >
              <span className="dt-group__name">{g.blueprint}</span>
              <span className="dt-group__count t-data">{g.routes.length}</span>
            </button>
            {expanded(g.blueprint) && (
              <table className="dt-table">
                <thead>
                  <tr>
                    <th style={{ width: '7rem' }}>Methods</th>
                    <th>Rule</th>
                    <th style={{ width: '13rem' }}>Guard</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {g.routes.map((r) => {
                    const id = `${r.rule}-${r.methods.join()}`
                    const opened = openRoute === id
                    return (
                      <Fragment key={id}>
                        <tr className={`dt-row${opened ? ' is-open' : ''}`}
                            aria-expanded={opened}
                            onClick={() => setOpenRoute(opened ? null : id)}>
                          <td className="t-data">{r.methods.join(', ')}</td>
                          <td className="dt-rule">{r.rule}</td>
                          <td className={r.guard === 'public' ? 'dt-public' : ''}>
                            {r.guard_label}{r.org_scoped ? ' · org' : ''}
                          </td>
                          <td className="muted">{r.summary ?? ''}</td>
                        </tr>
                        {opened && (
                          <tr className="dt-row__detail">
                            <td colSpan={4}><RouteDetail r={r} /></td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </section>

      <section className="dt-section">
        <h2 className="dt-h2">OpenAPI</h2>
        <p className="muted dt-note">
          The same reference as an{' '}
          <a href="/api/v1/developer-tools/openapi.json">OpenAPI 3.0 document</a>{' '}
          — import it into Swagger UI, Postman, or an SDK generator. Generated
          from the same live inventory as the table above, so the two cannot
          disagree.
        </p>
      </section>

      <section className="dt-section">
        <h2 className="dt-h2">Example requests</h2>
        {/* Authorization: Bearer is what app/helpers/token_auth.py reads;
            this section said X-API-Key, which no route has ever accepted
            since the token rework. */}
        <pre className="dt-code">{`curl -H "Authorization: Bearer $PRIORI_API_TOKEN" \\
     "${window.location.origin}/api/v1/games"`}</pre>
        <pre className="dt-code">{`import os, requests

r = requests.get(
    "${window.location.origin}/api/v1/games",
    headers={"Authorization": f"Bearer {os.environ['PRIORI_API_TOKEN']}"},
    timeout=30,
)
r.raise_for_status()
print(r.json())`}</pre>
        <p className="muted dt-note">
          Session-guarded routes are a different mechanism: they read an
          httpOnly cookie set at sign-in and are meant for the browser, not for
          scripts. A script wanting those needs an API key and a route that
          accepts one.
        </p>
      </section>
    </>
  )
}
